import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { Permission } from "src/api/Tmd.dtos";
import AddPageSubMenu from "src/components/layouts/AddPageSubMenu";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import useHasPermission from "src/hooks/authentication/useHasPermission";
import useRoleById from "src/hooks/data/load/useRoleById";
import { createRoleSubMenu } from "src/routing/navigation";
import { parseRouteId } from "src/utilities/parseRouteId";

/**
 * Registers the role submenu through the shared layout adapter.
 *
 * Postconditions: A rendered registration requests layout updates after render and removes its
 * submenu code on cleanup. Navigation factories retain responsibility for link metadata.
 * The role request is disabled without the system-role update permission; registration also
 * requires that permission and a role from which the factory can construct a submenu.
 */
const RoleSubMenu: React.FC = () => {
    const { roleId: roleIdParam } = useParams<{ roleId?: string }>();
    const roleId = parseRouteId(roleIdParam);
    const permissions = useCurrentUserPermissions();
    const canUpdateSystemRoles = useHasPermission(permissions, Permission.RolesUpdateSystem);
    const { data: role } = useRoleById(canUpdateSystemRoles ? roleId : null);
    const subMenu = useMemo(() => createRoleSubMenu(role), [role]);

    if (!canUpdateSystemRoles || !subMenu) {
        return null;
    }

    return <AddPageSubMenu subMenu={subMenu} />;
};

export default RoleSubMenu;
